/**
 * Break-glass — emergency access, as an incident record.
 *
 * Replaces `Privileges.GetBreakGlassStatuses`, `Privileges.StartBreakGlass` and
 * `Privileges.EndBreakGlass`, and adds what main has no call for: a second
 * administrator's approval of an organization-wide incident, and extending one.
 *
 * Break-glass is deliberately not a scope: a bearer scope is a fixed string and
 * this is a temporal grant. The incident record IS the grant: while it is in
 * effect the subject's own requests resolve the view-only permissions it lent
 * (main #329; decision D8). There is no incident token. A single-chart incident
 * (`patient_id`) lends them for that chart only.
 *
 * There is no delete. `EndBreakGlass` removed the subject's overrides and
 * restored a snapshot, destroying anything created during the window — so the
 * one record a compliance review needs was the record the system erased.
 * Closing an incident creates a revocation instead.
 *
 * Every call takes the incident's `public_id`, never a user id.
 */

import api from './client';

/** The review queue. `?filter[status]=`, `?filter[target_user_id]=`, `?filter[patient_id]=`. */
export function listBreakGlassIncidents(params = {}) {
  return api.list('/break-glass-incidents', params);
}

export function getBreakGlassIncident(incidentId) {
  return api.get(`/break-glass-incidents/${encodeURIComponent(incidentId)}`);
}

/** `{target_user_id, patient_id?, reason, reason_code?, duration_minutes?}`. Never an approver. */
export function startBreakGlass(payload) {
  return api.post('/break-glass-incidents', payload);
}

/** `{reason}`. */
export function revokeBreakGlass(incidentId, payload) {
  return api.post(`/break-glass-incidents/${encodeURIComponent(incidentId)}/revocation`, payload);
}

/**
 * The second administrator's sign-off on an organization-wide incident, made
 * by them, in their own request. Neither its requester nor its subject may.
 */
export function approveBreakGlass(incidentId) {
  return api.post(`/break-glass-incidents/${encodeURIComponent(incidentId)}/approval`, {});
}

/**
 * A later `expires_at` (ISO 8601), never past eight hours from the start;
 * `reason` is optional and lands on the incident's alert.
 */
export function extendBreakGlass(incidentId, { expiresAt, reason } = {}) {
  return api.patch(`/break-glass-incidents/${encodeURIComponent(incidentId)}`, {
    expires_at: expiresAt,
    ...(reason ? { reason } : {}),
  });
}

export default {
  listBreakGlassIncidents,
  getBreakGlassIncident,
  startBreakGlass,
  revokeBreakGlass,
  approveBreakGlass,
  extendBreakGlass,
};
